/**
 * «Что происходит» — Spec 013 §8.
 *
 * One screen that answers the questions a person asks after being away: what
 * is waiting for me, what is waiting for my partner, who is doing what and at
 * which stage, where work stalled, what is only prepared versus confirmed, and
 * what changed since I last looked. Everything comes from the same overview the
 * bot and the agents read; the screen never guesses beyond it.
 *
 * States are always words and icons, never colour alone (ui-ux-pro-max §1).
 */

import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  CircleCheck,
  FolderKanban,
  Hand,
  History,
  Hourglass,
  OctagonAlert,
  RotateCw,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Overview } from "@/api/resources";
import type { ActiveTask, ItemRef, OverviewOut, WaitingItem } from "@/api/types";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, ClickableCard } from "@/components/ui/Card";
import { EmptyState, ErrorState } from "@/components/ui/EmptyState";
import { ScreenTitle, Section } from "@/components/ui/Section";
import { SkeletonList } from "@/components/ui/Skeleton";
import { formatDateTime, parseDate, pluralRu } from "@/lib/format";

const WAITING_LABEL: Record<WaitingItem["kind"], string> = {
  result_review: "Проверить результат",
  idea_review: "Решить по идее",
  request: "Запрос",
  request_answered: "Ответ получен",
  agent_request: "Агент просит",
  code_review: "Согласовать код",
  // Task d9165be7: my own open task past its deadline.
  overdue: "Просрочено",
  // Spec 043: a staff member's new version waits for my «Одобрить».
  agent_approval: "Одобрить версию",
  // Spec 044: merged code of my task waits for my «Выпустить».
  code_release: "Выпустить",
  // Spec 055: a unit's kit version waits for one «да» of the top unit's people.
  unit_kit: "Одобрить набор отдела",
};

export function linkFor(item: ItemRef): string {
  switch (item.kind) {
    case "task":
      return `/tasks/${item.id}`;
    case "idea":
      return `/knowledge/sources/idea/${item.id}`;
    case "method":
      return `/knowledge/${item.id}`;
    case "note":
      return `/knowledge/sources/note/${item.id}`;
    case "project":
      return `/projects/${item.id}`;
    case "chat":
      return "/chat";
    case "case":
      return `/knowledge/cases/${item.id}`;
    // Spec 027: a card change opens the card, where its version waits for approval.
    case "agent":
      return `/agents/${item.id}`;
    // Spec 055: a unit's kit waits on the unit's page.
    case "department":
      return `/departments/${item.id}`;
    // Spec 018: the business passport and the resources live on «О бизнесе».
    case "business":
    case "resource":
      return "/business";
    default:
      return "/changes";
  }
}

function when(value: string | null | undefined): string {
  const date = parseDate(value ?? null);
  return date ? formatDateTime(date) : "—";
}

const MORE =
  "inline-flex min-h-touch items-center rounded-full border border-p-border bg-p-surface px-3 text-body font-semibold text-p-text press-surface";

export function OverviewView() {
  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: Overview.get,
    refetchOnMount: "always",
  });
  // A person looked: the next "changes since last look" starts from the
  // snapshot shown. Only a response fetched for this visit counts — cached
  // data from an earlier visit would move the marker past unseen changes.
  const marked = useRef(false);
  const shown = overview.isFetchedAfterMount ? overview.data?.generated_at : undefined;
  useEffect(() => {
    if (shown && !marked.current) {
      marked.current = true;
      Overview.seen(shown).catch(() => undefined);
    }
  }, [shown]);

  return (
    <div className="space-y-4 pt-2">
      <ScreenTitle
        eyebrow={overview.data ? `Что происходит · ${when(overview.data.generated_at)}` : "Что происходит"}
        trailing={
          <Button
            variant="ghost"
            size="compact"
            onClick={() => void overview.refetch()}
            loading={overview.isFetching}
            loadingText="Обновляем…"
            icon={<RotateCw className="h-4 w-4" aria-hidden="true" />}
          >
            Обновить
          </Button>
        }
      >
        Сейчас
      </ScreenTitle>

      {/* Spec 020 D5 — the inbox and the change log keep their pages. */}
      <nav aria-label="Ещё" className="flex flex-wrap gap-2">
        <Link to="/inbox" className={MORE}>Входящие: идеи и заметки</Link>
        <Link to="/changes" className={MORE}>Изменения</Link>
      </nav>

      {overview.isLoading && <SkeletonList count={4} />}
      {overview.isError && !overview.data && (
        <ErrorState
          title="Сводка не загрузилась"
          onRetry={() => void overview.refetch()}
          retrying={overview.isFetching}
        />
      )}
      {overview.data && <OverviewBody data={overview.data} />}
    </div>
  );
}

export function OverviewBody({ data }: { data: OverviewOut }) {
  const others = useMemo(() => {
    const groups = new Map<string, WaitingItem[]>();
    for (const item of data.for_others) {
      groups.set(item.waiting_on.name, [...(groups.get(item.waiting_on.name) ?? []), item]);
    }
    return [...groups.entries()];
  }, [data.for_others]);

  return (
    <>
      <Section title="Ждёт тебя" trailing={<Count n={data.for_me.length} />}>
        {data.for_me.length === 0 ? (
          <Card>
            <p className="text-body text-hint">Ничего не ждёт твоего решения.</p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {data.for_me.map((item) => (
              <WaitingRow key={`${item.kind}-${item.request_id ?? item.item.id}`} item={item} mine />
            ))}
          </ul>
        )}
      </Section>

      {others.map(([name, items]) => (
        <Section key={name} title={`Ждёт: ${name}`} trailing={<Count n={items.length} />}>
          <ul className="space-y-2">
            {items.map((item) => (
              <WaitingRow key={`${item.kind}-${item.request_id ?? item.item.id}-${name}`} item={item} />
            ))}
          </ul>
        </Section>
      ))}

      <Section title="В работе" trailing={<Count n={data.active.length} />}>
        {data.active.length === 0 ? (
          <EmptyState
            icon={<Wrench className="h-8 w-8" aria-hidden="true" />}
            title="Сейчас никто ничего не ведёт"
            description="Возьми задачу в разделе «Работа» или запиши идею."
          />
        ) : (
          <ul className="space-y-2">
            {data.active.map((task) => (
              <ActiveRow key={task.item.id} task={task} />
            ))}
          </ul>
        )}
        {data.unassigned_open > 0 && (
          <Link to="/work" className="flex min-h-touch items-center text-meta text-accent">
            Свободных задач: {data.unassigned_open} — открыть «Работу»
          </Link>
        )}
      </Section>

      {data.recently_done.length > 0 && (
        <Section title="Закрыто за неделю" trailing={<Count n={data.recently_done.length} />}>
          <ul className="space-y-2">
            {data.recently_done.map((task) => (
              <li key={task.item.id}>
                <ClickableCard to={linkFor(task.item)}>
                  <p className="text-cell-title font-medium text-text">{task.item.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {task.result_state === "confirmed" && (
                      <Badge tone="accent" icon={<CircleCheck className="h-3 w-3" aria-hidden="true" />}>
                        результат подтверждён
                      </Badge>
                    )}
                    {task.result_state === "pending" && (
                      <Badge tone="danger" icon={<Hourglass className="h-3 w-3" aria-hidden="true" />}>
                        результат ждёт подтверждения
                      </Badge>
                    )}
                    {task.result_state === "skipped" && <Badge>закрыта без проверки</Badge>}
                    {task.result_state === "none" && <Badge>закрыта без результата</Badge>}
                    <span className="text-meta text-hint">{when(task.completed_at)}</span>
                  </div>
                </ClickableCard>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title={`Изменения других с ${when(data.since)}`} trailing={<Count n={data.changes_total} />}>
        {data.changes.length === 0 ? (
          <Card>
            <p className="text-body text-hint">С прошлого просмотра ничего нового.</p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {data.changes.map((group) => (
              <li key={`${group.item.kind}-${group.item.id}`}>
                <ClickableCard to={linkFor(group.item)}>
                  <div className="flex items-start gap-2">
                    <History className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-cell-title font-medium text-text">
                        {group.item.project ? `${group.item.project} › ` : ""}
                        {group.item.title}
                      </p>
                      <p className="text-meta text-hint">
                        {group.count} {pluralRu(group.count, "изменение", "изменения", "изменений")} ·{" "}
                        {group.actors.join(", ")} · {when(group.last_at)}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {group.headlines.map((line, i) => (
                          <li key={i} className="text-body text-text">
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </ClickableCard>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {data.projects.length > 0 && (
        <Section title="По проектам">
          <ul className="space-y-2">
            {data.projects.map((project) => (
              <li key={project.id}>
                <Card>
                  <div className="flex items-start gap-2">
                    <FolderKanban className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/projects/${project.id}`}
                        className="flex min-h-touch items-center text-cell-title font-medium text-text"
                      >
                        {project.title}
                      </Link>
                      <p className="text-meta text-hint">
                        Открыто: {project.open} · в работе: {project.in_progress}
                        {project.blocked > 0 ? ` · с препятствием: ${project.blocked}` : ""}
                      </p>
                      {project.next_steps.length > 0 && (
                        <ul className="mt-1 space-y-1">
                          {project.next_steps.map((step) => (
                            <li key={step.item.id} className="text-body text-text">
                              <span className="text-hint">Дальше · </span>
                              <Link to={linkFor(step.item)} className="underline decoration-hint/40">
                                {step.item.title}
                              </Link>
                              : {step.text}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

function Count({ n }: { n: number }) {
  return <span className="text-meta text-hint">{n}</span>;
}

function WaitingRow({ item, mine = false }: { item: WaitingItem; mine?: boolean }) {
  const Icon = mine ? Hand : Hourglass;
  return (
    <li>
      <ClickableCard to={linkFor(item.item)}>
        <div className="flex items-start gap-2">
          <Icon
            className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${mine ? "text-accent" : "text-hint"}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-meta text-hint">{WAITING_LABEL[item.kind]}</p>
            <p className="text-cell-title font-medium text-text">
              {item.item.project ? `${item.item.project} › ` : ""}
              {item.item.title}
            </p>
            {item.text && <p className="text-body text-text">{item.text}</p>}
            {item.answer && <p className="text-body text-text">Ответ: {item.answer}</p>}
            {item.by && (
              <p className="flex items-center gap-1 text-meta text-hint">
                {item.via_agent && <Bot className="h-3.5 w-3.5" aria-hidden="true" />}
                {item.via_agent ? `агент (${item.by.name})` : item.by.name} · {when(item.since)}
              </p>
            )}
          </div>
        </div>
      </ClickableCard>
    </li>
  );
}

function ActiveRow({ task }: { task: ActiveTask }) {
  const entry = task.last_entry;
  const blocked = task.flags.includes("blocked");
  return (
    <li>
      <ClickableCard to={linkFor(task.item)}>
        <div className="flex items-start gap-2">
          {blocked ? (
            <OctagonAlert className="mt-0.5 h-[18px] w-[18px] shrink-0 text-dstr" aria-hidden="true" />
          ) : (
            <Wrench className="mt-0.5 h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
          )}
          <div className="min-w-0 space-y-1">
            <p className="text-cell-title font-medium text-text">{task.item.title}</p>
            <p className="text-meta text-hint">
              {task.assignee ? task.assignee.name : "без исполнителя"} · {task.status} · обновлено{" "}
              {when(task.last_activity_at)}
            </p>
            {entry && (
              <dl className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-body">
                {entry.current_stage ? (
                  <>
                    <dt className="text-hint">Сейчас</dt>
                    <dd className="text-text">{entry.current_stage}</dd>
                  </>
                ) : (
                  entry.summary && (
                    <>
                      <dt className="text-hint">Последнее</dt>
                      <dd className="text-text">{entry.summary}</dd>
                    </>
                  )
                )}
                {entry.next_step && (
                  <>
                    <dt className="text-hint">Дальше</dt>
                    <dd className="text-text">{entry.next_step}</dd>
                  </>
                )}
                {entry.blocker && (
                  <>
                    <dt className="text-dstr">Препятствие</dt>
                    <dd className="text-text">{entry.blocker}</dd>
                  </>
                )}
              </dl>
            )}
            {task.notes
              .filter((note) => !(blocked && note.startsWith("Есть препятствие")))
              .map((note, i) => (
                <p key={`${i}-${note}`} className="flex items-start gap-1 text-meta text-text">
                  {/* Spec 017: «Ждёт „A“ · у Сани» — waiting for another task. */}
                  {note.startsWith("Ждёт «") ? (
                    <Hourglass className="mt-0.5 h-3.5 w-3.5 shrink-0 text-dstr" aria-hidden="true" />
                  ) : (
                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-hint" aria-hidden="true" />
                  )}
                  {note}
                </p>
              ))}
          </div>
        </div>
      </ClickableCard>
    </li>
  );
}
